import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../component-styles/Contacts.css';
import { Plus, Pencil, ChevronUp, ChevronDown, Mail, Trash2, Users, Loader2 } from 'lucide-react';
import { Link2Icon, MagnifyingGlassIcon, Cross2Icon } from '@radix-ui/react-icons';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';

const DEFAULT_AVATAR =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 24 24'><rect fill='%23e6f1ec' width='24' height='24' rx='12'/><path fill='%23666' d='M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 2c-3 0-6 1.5-6 3.5V20h12v-2.5c0-2-3-3.5-6-3.5z'/></svg>";

function normalizeWebsite(url) {
  return url.replace(/^https?:\/\//, '');
}

// `vendorsOnly` is the dedicated Vendors tab (/vendors): the same contact list,
// locked to Vendor contacts, without the Type/Project columns that would be
// identical or blank on every row.
export default function Contacts({ vendorsOnly = false }) {
  const defaultType = vendorsOnly ? 'Vendor' : 'Client';
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(vendorsOnly ? 'Vendor' : 'All');
  const [sortBy, setSortBy] = useState({ field: 'name', dir: 'asc' });
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [form, setForm] = useState({
    name: '',
    type: defaultType,
    company: '',
    email: '',
    phone: '',
    ext: '',
    website: '',
    accountNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
  });
  const location = useLocation();
  const navigate = useNavigate();

  /* ---------------------------
     Fetch Contacts
  ---------------------------- */
  const fetchContacts = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    setLoading(true);
    try {
      const [contactsRes, projectsRes] = await Promise.all([
        fetch(`${API_BASE}/api/contacts/${encodeURIComponent(userId)}`, { cache: 'no-store', credentials: 'include' }),
        fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`, { cache: 'no-store', credentials: 'include' }),
      ]);
      const data = await contactsRes.json();
      const arr = Array.isArray(data) ? data : data.Items || [];
      const normalized = arr.map((c) => ({
        id: c.id || c.contactId,
        name: c.name || '',
        type: c.type || '',
        company: c.company || '',
        email: c.email || '',
        phone: c.phone || '',
        ext: c.ext || '',
        website: c.website || '',
        accountNumber: c.accountNumber || '',
        streetAddress: c.streetAddress || '',
        city: c.city || '',
        state: c.state || '',
        zip: c.zip || '',
        notes: c.notes || '',
        linkedEntityId: c.linkedEntityId || '',
        linkedEntityType: c.linkedEntityType || '',
      }));
      setContacts(normalized);

      if (projectsRes.ok) {
        const pdata = await projectsRes.json();
        const parr = Array.isArray(pdata) ? pdata : pdata.Items || [];
        setProjects(parr);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    const editContactId = location.state?.editContactId;
    if (!editContactId || contacts.length === 0) return;

    const match = contacts.find((c) => c.id === editContactId);
    if (match) {
      openModal(match);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [contacts, location.state, location.pathname, navigate]);

  // Close modal on Escape
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal]);

  /* ---------------------------
     Sorting & Filtering
  ---------------------------- */
  function handleSort(field) {
    setSortBy((s) =>
      s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }
    );
  }

  const sortIcon = (field) => {
    if (sortBy.field !== field) return null;
    return sortBy.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    let list = contacts.filter((c) => {
      if (typeFilter !== 'All' && c.type !== typeFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.company.toLowerCase().includes(q)
      );
    });

    list.sort((a, b) => {
      const fa = (a[sortBy.field] || '').toLowerCase();
      const fb = (b[sortBy.field] || '').toLowerCase();
      if (fa < fb) return sortBy.dir === 'asc' ? -1 : 1;
      if (fa > fb) return sortBy.dir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [contacts, query, typeFilter, sortBy]);

  const projectsByCustomerId = useMemo(() => {
    const map = {};
    for (const p of projects) {
      const cid = p.customerId;
      const cname = (p.customerName || '').toLowerCase();
      const pname = p.projectNickname || p.name || '';
      if (!pname) continue;
      if (cid) {
        if (!map[cid]) map[cid] = [];
        if (!map[cid].includes(pname)) map[cid].push(pname);
      }
      if (cname) {
        const key = `name:${cname}`;
        if (!map[key]) map[key] = [];
        if (!map[key].includes(pname)) map[key].push(pname);
      }
    }
    return map;
  }, [projects]);

  function getContactProjects(c) {
    if (c.linkedEntityType === 'customer' && c.linkedEntityId) {
      const names = projectsByCustomerId[c.linkedEntityId] || [];
      if (names.length) return names.join(', ');
    }
    if (c.name) {
      const names = projectsByCustomerId[`name:${c.name.toLowerCase()}`] || [];
      return names.join(', ');
    }
    return '';
  }

  /* ---------------------------
     Copy Email
  ---------------------------- */
  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      const toast = document.createElement('div');
      toast.textContent = 'Copied!';
      toast.className = 'copy-toast-fixed';
      document.body.appendChild(toast);
      setTimeout(() => {
        toast.classList.add('fade');
        setTimeout(() => toast.remove(), 400);
      }, 900);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  /* ---------------------------
     Modal + Form Handling
  ---------------------------- */
  const openModal = (contact = null) => {
    setDuplicateWarning(null);
    setConfirmDeleteId(null);
    setForm(
      contact || {
        name: '',
        type: defaultType,
        company: '',
        email: '',
        phone: '',
        ext: '',
        website: '',
        accountNumber: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        notes: '',
      }
    );
    setEditingId(contact ? contact.id : null);
    setShowModal(true);
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleDelete(contact) {
    setConfirmDeleteId(null);
    const userId = localStorage.getItem('userId');
    try {
      const res = await fetch(`${API_BASE}/api/contacts/${encodeURIComponent(contact.id)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete contact');
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (err) {
      console.error('Error deleting contact:', err);
      notify('Error deleting contact.');
    }
  }

  async function doSave() {
    const userId = localStorage.getItem('userId');
    const payload = { ...form, userId };
    try {
      const res = await fetch(
        editingId
          ? `${API_BASE}/api/contacts/${encodeURIComponent(editingId)}`
          : `${API_BASE}/api/contacts`,
        {
          method: editingId ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error('Failed to save contact');
      await fetchContacts();
      setShowModal(false);
    } catch (err) {
      console.error('Error saving contact:', err);
      notify('Error saving contact.');
    }
  }

  async function handleSave() {
    if (!editingId && !duplicateWarning) {
      const nameLower = form.name.trim().toLowerCase();
      const duplicate = contacts.find(
        (c) => c.name.toLowerCase() === nameLower && c.type === form.type
      );
      if (duplicate) {
        setDuplicateWarning(`A ${form.type} named "${form.name}" already exists. Add anyway?`);
        return;
      }
    }
    setDuplicateWarning(null);
    await doSave();
  }

  /* ---------------------------
     UI
  ---------------------------- */
  return (
    <div className="contacts-page">
      {/* ---------- Header ---------- */}
      <header className="contacts-header">
        <div className="contacts-header-left">
          <h1 className="contacts-title">{vendorsOnly ? 'Vendors' : 'Contacts'}</h1>
          <p className="contacts-subtitle">
            {vendorsOnly ? 'Manage your vendors' : 'Manage your clients and vendors'}
          </p>
        </div>

        <div className="contacts-header-right">
          <div className="search-wrap">
            <MagnifyingGlassIcon />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, company, email..."
            />
            {query && (
              <button className="clear-btn" onClick={() => setQuery('')}>
                <Cross2Icon />
              </button>
            )}
          </div>

          {!vendorsOnly && (
            <select
              className="filter-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              {['All', 'Client', 'Vendor', 'Marketing', 'Other'].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          )}

          <button className="btn-add" onClick={() => openModal()}>
            <Plus size={14} /> Add New
          </button>
        </div>
      </header>

      {/* ---------- Table ---------- */}
      <section className="contacts-table-section">
        <div className="contacts-table-card">
          <table className="contacts-table">
            <thead>
              <tr>
                <th></th>
                <th className="sortable-th" onClick={() => handleSort('name')}>
                  CONTACT NAME {sortIcon('name')}
                </th>
                {!vendorsOnly && (
                  <th className="sortable-th" onClick={() => handleSort('type')}>
                    CONTACT TYPE {sortIcon('type')}
                  </th>
                )}
                <th className="sortable-th" onClick={() => handleSort('company')}>
                  COMPANY {sortIcon('company')}
                </th>
                {!vendorsOnly && <th>PROJECT</th>}
                <th>EMAIL</th>
                <th>PHONE</th>
                <th>EXT</th>
                <th>WEBSITE</th>
                <th>ACCOUNT #</th>
                <th>NOTES</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={vendorsOnly ? 10 : 12}>
                    <div className="contacts-empty-state">
                      <Loader2 size={28} className="contacts-loading-icon" />
                      <div className="contacts-empty-title">
                        {vendorsOnly ? 'Loading vendors…' : 'Loading contacts…'}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={vendorsOnly ? 10 : 12}>
                    <div className="contacts-empty-state">
                      <Users size={36} className="contacts-empty-icon-svg" />
                      <div className="contacts-empty-title">
                        {vendorsOnly ? 'No vendors yet' : 'No contacts yet'}
                      </div>
                      <div className="contacts-empty-sub">
                        {vendorsOnly
                          ? 'Add your first vendor to get started.'
                          : 'Add your first contact to get started.'}
                      </div>
                      <button className="btn-add contacts-empty-cta" onClick={() => openModal()}>
                        <Plus size={14} /> {vendorsOnly ? 'Add Vendor' : 'Add Contact'}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="avatar-cell">
                      <img src={DEFAULT_AVATAR} alt="" className="contacts-thumbnail" />
                    </td>
                    <td>{c.name}</td>
                    {!vendorsOnly && <td>{c.type}</td>}
                    <td>{c.company}</td>
                    {!vendorsOnly && <td>{getContactProjects(c)}</td>}

                    <td className="email-cell">
                      {c.email && (
                        <div className="copy-wrapper">
                          <span className="email-text">{c.email}</span>
                          <button
                            className="copy-btn"
                            onClick={() => handleCopyEmail(c.email)}
                            title="Copy email"
                          >
                            <Mail size={14} strokeWidth={1.8} className="mail-icon" />
                          </button>
                        </div>
                      )}
                    </td>

                    <td>{c.phone}</td>
                    <td>{c.ext}</td>
                    <td>
                      {c.website && (
                        <a href={`https://${normalizeWebsite(c.website)}`} target="_blank" rel="noreferrer">
                          <Link2Icon />
                        </a>
                      )}
                    </td>
                    <td>{c.accountNumber}</td>
                    <td className="notes-cell">{c.notes}</td>
                    <td className="actions-cell">
                      {confirmDeleteId === c.id ? (
                        <div className="confirm-delete">
                          <span className="confirm-delete-label">Delete?</span>
                          <button className="confirm-yes-btn" onClick={() => handleDelete(c)}>Yes</button>
                          <button className="confirm-no-btn" onClick={() => setConfirmDeleteId(null)}>No</button>
                        </div>
                      ) : (
                        <>
                          <button className="edit-btn" onClick={() => openModal(c)}>
                            <Pencil size={14} />
                          </button>
                          <button className="delete-btn" onClick={() => setConfirmDeleteId(c.id)} title="Delete contact">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---------- Modal ---------- */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <form
            className="contact-modal"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="contact-modal-head">
              <h2>{editingId ? 'Edit Contact' : 'Add Contact'}</h2>
              <button
                type="button"
                className="contact-modal-close"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="contact-form-grid">
              <div>
                <label>Name</label>
                <input name="name" value={form.name} onChange={handleChange} required />
              </div>

              <div>
                <label>Type</label>
                <select name="type" value={form.type} onChange={handleChange}>
                  {['Client', 'Vendor', 'Marketing', 'Other'].map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label>Company</label>
                <input name="company" value={form.company} onChange={handleChange} />
              </div>

              <div>
                <label>Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} />
              </div>

              <div>
                <label>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} />
              </div>

              <div>
                <label>Ext</label>
                <input name="ext" value={form.ext} onChange={handleChange} />
              </div>

              <div>
                <label>Website</label>
                <input name="website" value={form.website} onChange={handleChange} />
              </div>

              <div>
                <label>Account #</label>
                <input name="accountNumber" value={form.accountNumber} onChange={handleChange} />
              </div>

              {/* Saved on Vendor contacts, this is what auto-fills the Vendor
                  block when the contact is picked on a purchase order. */}
              <div className="full">
                <label>Street</label>
                <input name="streetAddress" value={form.streetAddress || ''} onChange={handleChange} />
              </div>

              <div>
                <label>City</label>
                <input name="city" value={form.city || ''} onChange={handleChange} />
              </div>

              <div>
                <label>State</label>
                <input name="state" value={form.state || ''} onChange={handleChange} />
              </div>

              <div>
                <label>Zip</label>
                <input name="zip" value={form.zip || ''} onChange={handleChange} />
              </div>

              <div className="full">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Additional details..."
                />
              </div>
            </div>

            {duplicateWarning && (
              <div className="duplicate-warning">
                <span>{duplicateWarning}</span>
                <div className="duplicate-warning-actions">
                  <button type="button" className="btn cancel" onClick={() => setDuplicateWarning(null)}>
                    Go Back
                  </button>
                  <button type="button" className="btn submit" onClick={doSave}>
                    Add Anyway
                  </button>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn cancel" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn submit">
                {editingId ? 'Save Changes' : 'Add Contact'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
